<script setup lang="ts">
/**
 * One entry of a kit menu (NxContextMenu, the ribbon menus): optional icon,
 * label, optional shortcut hint on the right. `danger` marks a destructive
 * entry, `checked` (true/false) turns it into a checkable entry with a tick.
 *
 * Emits `select` and then closes the whole menu — `keepOpen` keeps it open
 * (a toggle the user may flip several times).
 *
 * Opt-in, and absent by default. `description` (prop or slot) is a muted
 * second line that may wrap; the `meta` slot is badges at the end of that
 * line. `secondaryAction` is a separate button in front of the label (a pin,
 * not a select): it emits `secondary` and leaves the menu open. ArrowRight
 * moves from the entry to that button, ArrowLeft moves back. ArrowUp and
 * ArrowDown still change entries, and ArrowLeft on the entry itself still
 * closes a submenu.
 */
import Check from 'lucide-vue-next/dist/esm/icons/check.js';
import { computed, inject, ref, useSlots, type Component } from 'vue';
import { nxMenuContextKey } from './menuContext';

// The row is the root whenever it is rendered. Fallthrough attributes
// (data-testid, class, listeners) belong on the menuitem button, as they did
// when that button was the root.
defineOptions({ inheritAttrs: false });

export interface NxContextMenuSecondaryAction {
  /** Icon on the secondary control. Decorative; `label` is the accessible name. */
  icon: Component;
  /** Accessible name and tooltip („Pin“, „Unpin“). */
  label: string;
  /** Toggle state. `true` or `false` sets `aria-pressed`, `true` also fills the icon; omit for a one-shot action. */
  pressed?: boolean;
}

const props = withDefaults(
  defineProps<{
    danger?: boolean;
    disabled?: boolean;
    /** `true`/`false` makes a checkable entry; unset is a plain one. */
    checked?: boolean;
    /** Keyboard hint on the right („⌘ C“) — text only, the caller binds the keys. */
    shortcut?: string;
    keepOpen?: boolean;
    /** Muted second line under the label. Wraps. The `description` slot overrides it. */
    description?: string;
    /** Leading control that does not select the entry. The menu stays open. */
    secondaryAction?: NxContextMenuSecondaryAction;
  }>(),
  {
    danger: false,
    disabled: false,
    checked: undefined,
    shortcut: undefined,
    keepOpen: false,
    description: undefined,
    secondaryAction: undefined,
  }
);

const emit = defineEmits<{ select: []; secondary: [] }>();
const menu = inject(nxMenuContextKey, null);
const slots = useSlots();
const mainEl = ref<HTMLButtonElement | null>(null);
const secondaryEl = ref<HTMLButtonElement | null>(null);

const hasDescription = computed(
  () => !!slots.description || (props.description != null && props.description !== '')
);
const hasSubline = computed(() => hasDescription.value || !!slots.meta);
const secondaryPressed = computed(() => {
  const pressed = props.secondaryAction?.pressed;
  return pressed === undefined ? undefined : pressed;
});

function activate() {
  if (props.disabled) return;
  emit('select');
  if (!props.keepOpen) menu?.close();
}

function activateSecondary() {
  if (props.disabled || !props.secondaryAction) return;
  emit('secondary');
}

function onMainKeydown(event: KeyboardEvent) {
  if (event.key !== 'ArrowRight' || !props.secondaryAction || props.disabled) return;
  event.preventDefault();
  event.stopPropagation();
  secondaryEl.value?.focus({ preventScroll: true });
}

function onSecondaryKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    event.stopPropagation();
    mainEl.value?.focus({ preventScroll: true });
    return;
  }
  if (event.key === 'ArrowRight') {
    event.preventDefault();
    event.stopPropagation();
    return;
  }
  // Up/Down/Home/End belong to the menu. Focus the entry first so the walk
  // starts there — this button is not one of the walked items.
  if (['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
    mainEl.value?.focus({ preventScroll: true });
  }
}
</script>

<template>
  <!-- With a secondary action the two buttons share a row (role="group");
       without one the entry is the single menuitem button it always was,
       with no wrapper element, so siblings and fallthrough attributes are
       exactly as before. -->
  <div v-if="props.secondaryAction" class="nx-context-menu-item-row has-secondary" role="group">
    <button
      ref="secondaryEl"
      type="button"
      class="nx-context-menu-item__secondary"
      :class="{ 'is-pressed': props.secondaryAction.pressed === true }"
      :aria-label="props.secondaryAction.label"
      :title="props.secondaryAction.label"
      :aria-pressed="secondaryPressed"
      :disabled="props.disabled"
      tabindex="-1"
      data-nx-menu-secondary
      data-keep-open
      @click.stop="activateSecondary"
      @keydown="onSecondaryKeydown"
    >
      <component :is="props.secondaryAction.icon" class="nxt-icon-sm" aria-hidden="true" />
    </button>
    <button
      ref="mainEl"
      v-bind="$attrs"
      type="button"
      class="nx-context-menu-item"
      :class="{
        'is-danger': props.danger,
        'is-checkable': props.checked !== undefined,
        'has-subline': hasSubline,
      }"
      :role="props.checked === undefined ? 'menuitem' : 'menuitemcheckbox'"
      :aria-checked="props.checked === undefined ? undefined : props.checked"
      :aria-disabled="props.disabled || undefined"
      :disabled="props.disabled"
      tabindex="-1"
      data-nx-menu-item
      @click="activate"
      @keydown="onMainKeydown"
    >
      <span
        v-if="props.checked !== undefined"
        class="nx-context-menu-item__check"
        aria-hidden="true"
      >
        <Check v-if="props.checked" class="nxt-icon-sm" />
      </span>
      <span v-if="$slots.icon" class="nx-context-menu-item__icon" aria-hidden="true">
        <slot name="icon" />
      </span>
      <span v-if="hasSubline" class="nx-context-menu-item__text">
        <span class="nx-context-menu-item__label"><slot /></span>
        <span class="nx-context-menu-item__sub">
          <span v-if="hasDescription" class="nx-context-menu-item__description">
            <slot name="description">{{ props.description }}</slot>
          </span>
          <span v-if="$slots.meta" class="nx-context-menu-item__meta">
            <slot name="meta" />
          </span>
        </span>
      </span>
      <span v-else class="nx-context-menu-item__label"><slot /></span>
      <kbd v-if="props.shortcut" class="nx-context-menu-item__shortcut">{{ props.shortcut }}</kbd>
    </button>
  </div>
  <button
    v-else
    ref="mainEl"
    v-bind="$attrs"
    type="button"
    class="nx-context-menu-item"
    :class="{
      'is-danger': props.danger,
      'is-checkable': props.checked !== undefined,
      'has-subline': hasSubline,
    }"
    :role="props.checked === undefined ? 'menuitem' : 'menuitemcheckbox'"
    :aria-checked="props.checked === undefined ? undefined : props.checked"
    :aria-disabled="props.disabled || undefined"
    :disabled="props.disabled"
    tabindex="-1"
    data-nx-menu-item
    @click="activate"
    @keydown="onMainKeydown"
  >
    <span v-if="props.checked !== undefined" class="nx-context-menu-item__check" aria-hidden="true">
      <Check v-if="props.checked" class="nxt-icon-sm" />
    </span>
    <span v-if="$slots.icon" class="nx-context-menu-item__icon" aria-hidden="true">
      <slot name="icon" />
    </span>
    <span v-if="hasSubline" class="nx-context-menu-item__text">
      <span class="nx-context-menu-item__label"><slot /></span>
      <span class="nx-context-menu-item__sub">
        <span v-if="hasDescription" class="nx-context-menu-item__description">
          <slot name="description">{{ props.description }}</slot>
        </span>
        <span v-if="$slots.meta" class="nx-context-menu-item__meta">
          <slot name="meta" />
        </span>
      </span>
    </span>
    <span v-else class="nx-context-menu-item__label"><slot /></span>
    <kbd v-if="props.shortcut" class="nx-context-menu-item__shortcut">{{ props.shortcut }}</kbd>
  </button>
</template>

<style scoped>
.nx-context-menu-item-row {
  display: contents;
}
.nx-context-menu-item-row.has-secondary {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
}
.nx-context-menu-item {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-sm);
  width: 100%;
  min-height: var(--nxt-control-size-sm);
  padding: var(--nxt-space-2xs) var(--nxt-space-sm);
  border: none;
  border-radius: var(--nxt-radius-md);
  background: transparent;
  color: var(--nxt-menu-item-fg, var(--nxt-fg));
  font: inherit;
  font-size: var(--nxt-menu-item-size, var(--nxt-font-size-sm));
  text-align: start;
  cursor: pointer;
  outline: none;
  transition: background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-context-menu-item-row.has-secondary > .nx-context-menu-item {
  width: auto;
  flex: 1 1 auto;
  min-width: 0;
  padding-inline-start: var(--nxt-space-2xs);
}
.nx-context-menu-item:hover:not(:disabled),
.nx-context-menu-item:focus-visible,
.nx-context-menu-item[aria-expanded='true'] {
  background: var(--nxt-menu-item-bg-hover);
}
.nx-context-menu-item:focus-visible {
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-context-menu-item.is-danger {
  color: var(--nxt-danger-subtle-fg);
}
.nx-context-menu-item.is-danger:hover:not(:disabled),
.nx-context-menu-item.is-danger:focus-visible {
  background: var(--nxt-danger-subtle-bg);
}
.nx-context-menu-item:disabled {
  color: var(--nxt-menu-item-fg-disabled, var(--nxt-fg-muted));
  cursor: not-allowed;
  opacity: 0.6;
}
.nx-context-menu-item__check,
.nx-context-menu-item__icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: var(--nxt-icon-size-md, calc(var(--nxt-size-unit) * 1.125));
  color: var(--nxt-fg-muted);
}
.nx-context-menu-item.is-danger .nx-context-menu-item__icon {
  color: inherit;
}
.nx-context-menu-item__check {
  color: var(--nxt-brand-subtle-fg);
}
.nx-context-menu-item__label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-context-menu-item__text {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: flex-start;
  gap: calc(var(--nxt-space-2xs) / 2);
  min-width: 0;
}
.nx-context-menu-item__text .nx-context-menu-item__label {
  flex: 0 1 auto;
  width: 100%;
  font-weight: var(--nxt-font-weight-medium);
}
.nx-context-menu-item__sub {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-2xs);
  max-width: 100%;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-regular);
  line-height: 1.35;
  white-space: normal;
}
.nx-context-menu-item__description {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
}
.nx-context-menu-item__meta {
  display: inline-flex;
  flex: 0 1 auto;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-2xs);
  max-width: 100%;
}
.nx-context-menu-item__shortcut {
  flex: 0 0 auto;
  color: var(--nxt-fg-muted);
  font-family: inherit;
  font-size: var(--nxt-font-size-xs);
  letter-spacing: 0.02em;
}
.nx-context-menu-item__secondary {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  align-self: center;
  width: var(--nxt-control-size-sm);
  height: var(--nxt-control-size-sm);
  margin-inline-start: var(--nxt-space-2xs);
  padding: 0;
  border: none;
  border-radius: var(--nxt-radius-md);
  background: transparent;
  color: var(--nxt-fg-muted);
  cursor: pointer;
  outline: none;
  transition: background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-context-menu-item__secondary.is-pressed {
  color: var(--nxt-fg);
}
/* A pressed toggle shows its icon filled (star, pin, heart). */
.nx-context-menu-item__secondary.is-pressed :deep(svg) {
  fill: currentColor;
}
.nx-context-menu-item__secondary:hover:not(:disabled),
.nx-context-menu-item__secondary:focus-visible {
  background: var(--nxt-menu-item-bg-hover);
  color: var(--nxt-fg);
}
.nx-context-menu-item__secondary:focus-visible {
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-context-menu-item__secondary:disabled {
  color: var(--nxt-menu-item-fg-disabled, var(--nxt-fg-muted));
  cursor: not-allowed;
  opacity: 0.6;
}
@media (prefers-reduced-motion: reduce) {
  .nx-context-menu-item,
  .nx-context-menu-item__secondary {
    transition: none;
  }
}
</style>
